// Translates app state into the query our own list endpoints accept, and back out of the URL.
// Not a general query-param utility -- every rule here is one our API defines.
//
// Two query strings, not one, which is what there is to translate: UrlQueryState is what the
// address bar carries (`page`, `pageSize`, `analystTypes=SAST`) and QueryRequestParams is what the
// API takes (`offset`, `filter=agent_type:in:sast`). Only `sort` happens to coincide.
//
// Split the way the server is: QueryRequest shapes a result set (sort, select, filter) and
// PageRequest adds one page of it (limit, offset), because shaping and paging are separate
// capabilities -- an export, a stream or a count takes the first without the second. So
// createHttpQueryTranslation is the shaping half and createPaginatedQueryTranslation extends it.

export interface UrlQueryState {
  sortField: string;
  sortDesc: boolean;
}

export interface UrlPageQueryState extends UrlQueryState {
  page: number;
  pageSize: number;
}

export interface QueryRequestParams {
  sort: string;
  filter: string[];
}

export interface PaginatedRequestParams extends QueryRequestParams {
  limit: number;
  offset: number;
}

export const SORT_DESC_PREFIX = "-";

const TERM_SEPARATOR = ":";
const VALUE_SEPARATOR = ",";

// Mirrors db_sdk's Operator enum, which is what the server accepts in a filter term. Listed in
// full rather than only what is used today: an incomplete vocabulary would push the next consumer
// back to a bare string, which is what this replaces.
export const FILTER_OPERATOR = {
  EQ: "eq",
  NE: "ne",
  IN: "in",
  NOT_IN: "not_in",
  GT: "gt",
  GTE: "gte",
  LT: "lt",
  LTE: "lte",
  ILIKE: "ilike",
} as const;

export type FilterOperator =
  (typeof FILTER_OPERATOR)[keyof typeof FILTER_OPERATOR];

export interface FilterDimension {
  field: string;
  operator?: FilterOperator;
  isAllowed?: (value: string) => boolean;
  toWire?: (value: string) => string;
}

export interface HttpQuerySpec<State extends UrlQueryState> {
  defaults: State;
  sortableFields: readonly string[];
  filters: Partial<Record<Extract<keyof State, string>, FilterDimension>>;
}

export interface PaginatedQuerySpec<State extends UrlPageQueryState>
  extends HttpQuerySpec<State> {
  pageSizes: readonly number[];
  maxPage: number;
}

export interface HttpQueryTranslation<State extends UrlQueryState> {
  parse: (search: string) => State;
  build: (state: State) => string;
  toQueryRequestParams: (state: State) => QueryRequestParams;
}

export interface PaginatedQueryTranslation<State extends UrlPageQueryState>
  extends HttpQueryTranslation<State> {
  toPaginatedRequestParams: (state: State) => PaginatedRequestParams;
}

const carriesNoDelimiter = (value: string) =>
  !value.includes(TERM_SEPARATOR) && !value.includes(VALUE_SEPARATOR);

export const serializeRequestParams = (
  params: Record<string, unknown>
): string => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }
    (Array.isArray(value) ? value : [value]).forEach((entry) =>
      search.append(key, String(entry))
    );
  });
  return search.toString();
};

export const createHttpQueryTranslation = <State extends UrlQueryState>(
  spec: HttpQuerySpec<State>
): HttpQueryTranslation<State> => {
  const { defaults, sortableFields, filters } = spec;
  const filterKeys = Object.keys(filters) as Extract<keyof State, string>[];

  const sortTerm = (state: State) =>
    `${state.sortDesc ? SORT_DESC_PREFIX : ""}${state.sortField}`;

  const toSortState = (sort: string) => {
    const sortDesc = sort.startsWith(SORT_DESC_PREFIX);
    const sortField = sortDesc ? sort.slice(SORT_DESC_PREFIX.length) : sort;
    return sortableFields.includes(sortField)
      ? { sortField, sortDesc }
      : { sortField: defaults.sortField, sortDesc: defaults.sortDesc };
  };

  const toValues = (values: string[], dimension: FilterDimension) =>
    values.filter(
      (value) =>
        !!value &&
        carriesNoDelimiter(value) &&
        (dimension.isAllowed?.(value) ?? true)
    );

  const parse = (search: string): State => {
    const query = new URLSearchParams(search);
    const state = { ...defaults, ...toSortState(query.get("sort") ?? "") };
    filterKeys.forEach((key) => {
      Object.assign(state, {
        [key]: toValues(query.getAll(key), filters[key] as FilterDimension),
      });
    });
    return state;
  };

  const build = (state: State): string => {
    const search = new URLSearchParams();
    if (
      state.sortField !== defaults.sortField ||
      state.sortDesc !== defaults.sortDesc
    ) {
      search.append("sort", sortTerm(state));
    }
    filterKeys.forEach((key) =>
      (state[key] as unknown as string[]).forEach((value) =>
        search.append(key, value)
      )
    );
    return search.toString();
  };

  const toQueryRequestParams = (state: State): QueryRequestParams => ({
    sort: sortTerm(state),
    filter: filterKeys.flatMap((key) => {
      const dimension = filters[key] as FilterDimension;
      const values = (state[key] as unknown as string[]).map(
        (value) => dimension.toWire?.(value) ?? value
      );
      if (!values.length) {
        return [];
      }
      const operator = dimension.operator ?? FILTER_OPERATOR.IN;
      return `${dimension.field}${TERM_SEPARATOR}${operator}${TERM_SEPARATOR}${values.join(VALUE_SEPARATOR)}`;
    }),
  });

  return { parse, build, toQueryRequestParams };
};

export const createPaginatedQueryTranslation = <State extends UrlPageQueryState>(
  spec: PaginatedQuerySpec<State>
): PaginatedQueryTranslation<State> => {
  const { defaults, pageSizes, maxPage } = spec;
  const shaping = createHttpQueryTranslation<State>(spec);

  const toPage = (value: string | null) => {
    const parsed = parseInt(String(value), 10);
    return parsed > 0 && parsed <= maxPage ? parsed : defaults.page;
  };

  const toPageSize = (value: string | null) => {
    const parsed = parseInt(String(value), 10);
    return pageSizes.includes(parsed) ? parsed : defaults.pageSize;
  };

  const parse = (search: string): State => {
    const query = new URLSearchParams(search);
    return {
      ...shaping.parse(search),
      page: toPage(query.get("page")),
      pageSize: toPageSize(query.get("pageSize")),
    };
  };

  const build = (state: State): string => {
    const search = new URLSearchParams();
    if (state.page !== defaults.page) {
      search.append("page", String(state.page));
    }
    if (state.pageSize !== defaults.pageSize) {
      search.append("pageSize", String(state.pageSize));
    }
    // Re-read rather than re-implement: the shaping half owns sort and the filter dimensions.
    new URLSearchParams(shaping.build(state)).forEach((value, key) =>
      search.append(key, value)
    );
    return search.toString();
  };

  const toPaginatedRequestParams = (state: State): PaginatedRequestParams => ({
    limit: state.pageSize,
    offset: (state.page - 1) * state.pageSize,
    ...shaping.toQueryRequestParams(state),
  });

  return {
    ...shaping,
    parse,
    build,
    toPaginatedRequestParams,
  };
};
